import React, { useEffect, useState } from 'react'
import { useTheme } from 'styled-components'
import axios from 'axios'
import { fetchAuthSession } from '@aws-amplify/auth'
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";



function PlanCard({
  name,
  price,
  priceNote,
  salePriceEl,
  percentageEl,
  features,
  isActive,
  onSelect,
  ctaLabel,
  ctaDisabled,
  ctaVariant = 'primary',
  onCtaClick
}) {
  const theme = useTheme();
  const cardClass = `border border-border rounded-xl p-5 bg-card text-foreground cursor-pointer transition focus:outline-none focus:ring-2 select-none ${isActive ? 'shadow-lg' : 'hover:shadow-md'}`;
  const ringColor = theme.colors.primary;
  const buttonStyle = ctaVariant === 'secondary'
    ? { backgroundColor: theme.colors.cardBgLight, color: theme.colors.textOnLight, borderColor: theme.colors.border }
    : { backgroundColor: theme.colors.primary, color: theme.colors.buttonTextOnPrimary, borderColor: theme.colors.primary };

  return (
    <div
      className={cardClass}
      style={isActive ? { borderColor: ringColor, boxShadow: `0 0 0 2px ${ringColor}` } : undefined}
      onClick={onSelect}
      role='button'
      aria-pressed={isActive}
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onSelect?.() }}
    >
      <div className='text-lg font-bold mb-2 flex items-center'>
        {name}
        {isActive ? <span className='ml-2 rounded-full px-2 py-0.5 text-xs font-semibold' style={{ backgroundColor: `${theme.colors.primary}20`, color: theme.colors.primary }}>Current</span> : null}
      </div>
      
      <div>
        <div className='text-2xl font-extrabold mb-2 text-foreground'>
          {price}
        </div>
        <div className='flex items-baseline gap-2 h-[24px]'>
        {percentageEl ? <div className='text-l text-muted-foreground'>{percentageEl}</div> : null}
        {salePriceEl ? <div className='text-l line-through' style={{ color: theme.colors.error }}>{salePriceEl}</div> : null}
        </div>
      </div>
      {priceNote ? <div className='text-xs text-slate-500 mb-3'>{priceNote}</div> : null}
      <div className='h-px my-3' style={{ backgroundColor: theme.colors.border }} />
      <div>
        {features.map((f, i) => (
          <div key={i} className='flex items-center gap-2 mb-2 text-sm text-slate-900 dark:text-gray-100'>
            <span className='w-[18px] h-[18px] rounded flex items-center justify-center text-[12px]' style={f.included ? { backgroundColor: theme.colors.successBg, color: theme.colors.success } : { backgroundColor: theme.colors.errorBg, color: theme.colors.error }}>{f.included ? '✓' : '×'}</span>
            <span className={f.included ? 'text-gray-900 dark:text-gray-100' : 'text-slate-400'}>{f.label}</span>
          </div>
        ))}
      </div>
      <button
        type='button'
        onClick={e => { e.stopPropagation(); onCtaClick?.() }}
        disabled={ctaDisabled}
        className={`w-full mt-3 py-2.5 px-3 rounded-xl font-bold border ${ctaDisabled ? 'opacity-55 cursor-not-allowed' : ''}`}
        style={buttonStyle}
        onMouseEnter={(e) => { if (!ctaDisabled && ctaVariant === 'secondary') e.currentTarget.style.backgroundColor = theme.colors.hover; }}
        onMouseLeave={(e) => { if (ctaVariant === 'secondary') e.currentTarget.style.backgroundColor = theme.colors.cardBgLight; }}
      >
        {ctaLabel}
      </button>
    </div>
  )
}

function CreditPackCard({ credits, price, onPurchase, disabled, loading, extra }) {
  const theme = useTheme();
  const cardClass = 'border border-border rounded-xl p-5 bg-card text-foreground transition hover:shadow-md'
  const formattedCredits = typeof credits === 'number' ? credits.toLocaleString() : credits
  const buttonStyle = disabled
    ? { backgroundColor: theme.colors.hover, color: theme.colors.muted, borderColor: theme.colors.border }
    : { backgroundColor: theme.colors.primary, color: theme.colors.buttonTextOnPrimary, borderColor: theme.colors.primary };

  return (
    <div className={cardClass}>
      <div className='text-sm font-semibold text-slate-500 mb-1'>Credit pack</div>
      <div className='text-xl font-extrabold mb-1'>{formattedCredits} credits</div>
      <div>
      <div className='font-bold mb-1'>
        {price}
      </div>
      <div className='text-sm font-bold mb-3' style={{ color: extra ? theme.colors.success : theme.colors.muted }}>
        {extra ? extra : '—'}
      </div>
      </div>
      <button
        type='button'
        onClick={() => onPurchase?.()}
        disabled={disabled}
        className={`w-full h-[46px] mt-1 py-2.5 px-3 rounded-xl font-bold border ${disabled ? 'cursor-not-allowed' : ''}`}
        style={buttonStyle}
      >
        {loading ? <LoadingSpinner /> : 'Purchase'}
      </button>
    </div>
  )
}

const PLAN_LABELS = {
  free: 'Free',
  starter: 'Starter',
  pro: 'Pro',
}

function formatPerMonth(value) {
  return Number.isInteger(value) ? `$${value}/mo` : `$${value.toFixed(2)}/mo`
}

export default function PlansView() {
  const theme = useTheme()
  const PlanPrices = {
    free: {
      price: 0,
    },
    starter: {
      monthly: {
        perMonth: 59,
      },
      yearly: {
        perMonth: 52.5,
        total: 630,
        comparePerMonth: 59,
        percentage: 11,
      },
    },
    pro: {
      monthly: {
        perMonth: 100,
      },
      yearly: {
        perMonth: 89,
        total: 1068,
        comparePerMonth: 100,
        percentage: 11,
      },
    },
  }
  const [currentPlan, setCurrentPlan] = useState('free') // 'free' | 'starter' | 'pro'
  const [billingInterval, setBillingInterval] = useState('monthly') // 'monthly' | 'yearly'
  const [loading, setLoading] = useState(false)
  const [creditsLoading, setCreditsLoading] = useState(false)
  const [subscription, setSubscription] = useState(null)
  const [credits, setCredits] = useState(0)
  const [packLoading, setPackLoading] = useState(null)
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001'

  const getAuthHeaders = async () => {
    try {
      const session = await fetchAuthSession()
      const token = session.tokens?.idToken?.toString()
      return token ? { Authorization: `Bearer ${token}` } : {}
    } catch {
      return {}
    }
  }

  const syncPlan = async () => {
    try {
      const headers = await getAuthHeaders()
      const { data } = await axios.post(`${apiBase}/billing/sync`, {}, { headers })
      const plan = data?.plan === 'starter' || data?.plan === 'pro' ? data.plan : 'free'
      setCurrentPlan(plan)
      setSubscription(data?.subscription || null)
      const interval = data?.subscription?.interval
      if ((plan === 'starter' || plan === 'pro') && (interval === 'monthly' || interval === 'yearly')) {
        setBillingInterval(interval)
      }
    } catch (e) {
      // fallback to free
      setCurrentPlan('free')
      setSubscription(null)
    }
  }

  const fetchCredits = async () => {
    setCreditsLoading(true)
    try {
      const headers = await getAuthHeaders()
      const { data } = await axios.get(`${apiBase}/billing/credits`, { headers })
      const value = typeof data?.credits === 'number' ? data.credits : 0
      setCredits(value)
    } catch (e) {
      setCredits(0)
    } finally {
      setCreditsLoading(false)
    }
  }

  useEffect(() => {
    syncPlan()
    fetchCredits()
    const params = new URLSearchParams(window.location.search)
    if (params.get('billing')) {
      syncPlan()
      fetchCredits()
    }
  }, [])

  const startCheckout = async (plan) => {
    setLoading(true)
    try {
      const headers = await getAuthHeaders()
      const { data } = await axios.post(`${apiBase}/billing/create-checkout-session`, { plan, interval: billingInterval }, { headers })
      if (data?.url) window.location.href = data.url
    } catch (e) {
      setLoading(false)
      alert('Failed to start checkout')
    }
  }

  const openPortal = async () => {
    setLoading(true)
    try {
      const headers = await getAuthHeaders()
      const { data } = await axios.post(`${apiBase}/billing/create-portal-session`, {}, { headers })
      if (data?.url) window.location.href = data.url
    } catch (e) {
      setLoading(false)
      alert('Failed to open billing portal')
    }
  }

  const startCreditPackCheckout = async (packId) => {
    setPackLoading(packId)
    try {
      const headers = await getAuthHeaders()
      const { data } = await axios.post(`${apiBase}/billing/create-credit-pack-session`, { packId }, { headers })
      if (data?.url) {
        window.location.href = data.url
        return
      }
      setPackLoading(null)
      alert('Failed to start credit pack checkout')
    } catch (e) {
      setPackLoading(null)
      alert('Failed to start credit pack checkout')
    }
  }

  // Master features list; Starter/Pro include all. Free shows the same list with limited items marked.
  const freeFeatures = [
    { label: '50 credits per month', free: true },
    { label: '1 channel integration', free: true },
    { label: 'Real-time reporting & analytics', free: true },
    { label: 'Website & ad performance insights', free: false },
    { label: 'Benchmarking and health scoring', free: true },
    { label: 'Email campaigns', free: true },
    { label: 'Advanced analytics & export', free: false },
    { label: 'Email support', free: true },
    { label: 'Priority phone support', free: false },
  ]

  const starterFeatures = [
    { label: '3000 credits per month', free: false },
    { label: 'All channels integration', free: false },
    { label: 'Real-time reporting & analytics', free: false },
    { label: 'Website & ad performance insights', free: false },
    { label: 'Benchmarking and health scoring', free: false },
    { label: 'Email campaigns', free: false },
    { label: 'Advanced analytics & export', free: false },
    { label: 'Email support', free: true },
    { label: 'Priority phone support', free: false },
  ]

  const proFeatures = [
    { label: '5000 credits per month', free: false },
    { label: 'All channels integration', free: false },
    { label: 'Real-time reporting & analytics', free: false },
    { label: 'Website & ad performance insights', free: false },
    { label: 'Benchmarking and health scoring', free: false },
    { label: 'Email campaigns', free: false },
    { label: 'Advanced analytics & export', free: false },
    { label: 'Email support', free: true },
    { label: 'Priority phone support', free: false },
  ]

  const isStarter = currentPlan === 'starter'
  const isPro = currentPlan === 'pro'
  const isPaid = isStarter || isPro
  const pendingCancel = isPaid && !!subscription?.cancelAtPeriodEnd
  const cancelAtIso = subscription?.currentPeriodEnd || subscription?.cancelAt || null
  const cancelAtLabel = cancelAtIso ? new Date(cancelAtIso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : null

  const isYearly = billingInterval === 'yearly'
  const starterMonthly = PlanPrices.starter.monthly
  const starterYearly = PlanPrices.starter.yearly
  const starterPerMonth = isYearly ? starterYearly.perMonth : starterMonthly.perMonth
  const starterPriceEl = formatPerMonth(starterPerMonth)
  const starterSalePriceEl = isYearly ? formatPerMonth(starterYearly.comparePerMonth) : null
  const starterPercentageEl = isYearly ? `(${starterYearly.percentage}% OFF)` : null
  const starterPriceNote = isYearly ? `Billed $${starterYearly.total.toLocaleString()}/year` : 'Billed monthly'

  const proMonthly = PlanPrices.pro.monthly
  const proYearly = PlanPrices.pro.yearly
  const proPerMonth = isYearly ? proYearly.perMonth : proMonthly.perMonth
  const proPriceEl = formatPerMonth(proPerMonth)
  const proSalePriceEl = isYearly ? formatPerMonth(proYearly.comparePerMonth) : null
  const proPercentageEl = isYearly ? `(${proYearly.percentage}% OFF)` : null
  const proPriceNote = isYearly ? `Billed $${proYearly.total.toLocaleString()}/year` : 'Billed monthly'

  const yearlySavePct = Math.max(starterYearly.percentage, proYearly.percentage)

  return (
    <div className='pt-8'>
      <div className='mb-4'>
        {/* <h2 className='m-0 text-[22px] font-bold'>Plans</h2> */}
        <div className='text-slate-500 text-sm mt-1'>Your current plan: <strong>{PLAN_LABELS[currentPlan] || 'Free'}</strong></div>
        <div className={`text-sm mt-1 ${creditsLoading ? 'flex items-center gap-2 text-slate-500' : (credits === 0 ? 'text-red-600' : (credits < 10 ? 'text-orange-600' : 'text-green-600'))}`}>Remaining credits: <strong>{creditsLoading ? <LoadingSpinner /> : credits}</strong></div>
        {pendingCancel ? (
          <div className='mt-2 text-xs rounded-md bg-yellow-50 text-yellow-800 border border-yellow-200 px-3 py-2'>
            Your {PLAN_LABELS[currentPlan]} subscription will end on <strong>{cancelAtLabel}</strong>. You will be downgraded to Free after this date.
          </div>
        ) : null}
      </div>

      <div className='flex items-center mt-4'>
        <div
          className='inline-flex rounded-xl p-1 border'
          role='tablist'
          aria-label='Billing interval'
          style={{ borderColor: theme.colors.border, backgroundColor: theme.colors.cardBgLight }}
        >
          <button
            type='button'
            role='tab'
            aria-selected={!isYearly}
            onClick={() => setBillingInterval('monthly')}
            className='px-4 py-1.5 rounded-lg text-sm font-semibold transition focus:outline-none'
            style={!isYearly
              ? { backgroundColor: theme.colors.primary, color: theme.colors.buttonTextOnPrimary }
              : { backgroundColor: 'transparent', color: theme.colors.muted }}
          >
            Monthly
          </button>
          <button
            type='button'
            role='tab'
            aria-selected={isYearly}
            onClick={() => setBillingInterval('yearly')}
            className='px-4 py-1.5 rounded-lg text-sm font-semibold transition focus:outline-none flex items-center gap-2'
            style={isYearly
              ? { backgroundColor: theme.colors.primary, color: theme.colors.buttonTextOnPrimary }
              : { backgroundColor: 'transparent', color: theme.colors.muted }}
          >
            Yearly
            <span
              className='rounded-full px-2 py-0.5 text-[11px] font-bold'
              style={isYearly
                ? { backgroundColor: theme.colors.buttonTextOnPrimary, color: theme.colors.primary }
                : { backgroundColor: theme.colors.successBg, color: theme.colors.success }}
            >
              Save {yearlySavePct}%
            </span>
          </button>
        </div>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4'>
        <PlanCard
          name='Free'
          salespriceEl=" "
          percentageEl=" "
          price='$0/mo'
          priceNote='Perfect to get started'
          features={freeFeatures.map(f => ({ label: f.label, included: f.free }))}
          isActive={!isPaid}
          onSelect={() => {}}
          ctaLabel={isPaid ? (loading ? 'Working…' : 'Downgrade to Free — $0/mo') : 'Current plan'}
          ctaDisabled={!isPaid || loading}
          ctaVariant='secondary'
          onCtaClick={async () => {
            if (!isPaid) return
            await openPortal() // Users downgrade via Stripe portal
          }}
        />

        <PlanCard
          name='Starter'
          salePriceEl={starterSalePriceEl}
          percentageEl={starterPercentageEl}
          price={starterPriceEl}
          priceNote={starterPriceNote}
          features={starterFeatures.map(f => ({ label: f.label, included: true }))}
          isActive={isStarter}
          onSelect={() => {}}
          ctaLabel={isStarter ? (loading ? 'Working…' : 'Manage') : (loading ? 'Working…' : `Upgrade to Starter — ${starterPriceEl}`)}
          ctaDisabled={loading}
          ctaVariant='primary'
          onCtaClick={async () => {
            if (isStarter) {
              await openPortal()
              return
            }
            await startCheckout('starter')
          }}
        />

        <PlanCard
          name='Pro'
          salePriceEl={proSalePriceEl}
          percentageEl={proPercentageEl}
          price={proPriceEl}
          priceNote={proPriceNote}
          features={proFeatures.map(f => ({ label: f.label, included: true }))}
          isActive={isPro}
          onSelect={() => {}}
          ctaLabel={isPro ? (loading ? 'Working…' : 'Manage') : (loading ? 'Working…' : `Upgrade to Pro — ${proPriceEl}`)}
          ctaDisabled={loading}
          ctaVariant='primary'
          onCtaClick={async () => {
            if (isPro) {
              await openPortal()
              return
            }
            await startCheckout('pro')
          }}
        />
      </div>

      <div className='mt-10'>
        <h3 className='m-0 text-[18px] font-bold'>One-time Credit Packs</h3>
        <div className='text-slate-500 text-sm mt-1'>
          Add extra credits on top of your monthly plan.
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4'>
          <CreditPackCard
            credits={1000}
            price='$25'
            extra={null}
            onPurchase={() => startCreditPackCheckout('pack_1000')}
            disabled={!!packLoading || loading}
            loading={packLoading === 'pack_1000'}
          />
          <CreditPackCard
            credits={5000}
            price='$99'
            extra='26% extra credits!'
            onPurchase={() => startCreditPackCheckout('pack_5000')}
            disabled={!!packLoading || loading}
            loading={packLoading === 'pack_5000'}
          />
          <CreditPackCard
            credits={25000}
            price='$399'
            extra='57% extra credits!'
            onPurchase={() => startCreditPackCheckout('pack_25000')}
            disabled={!!packLoading || loading}
            loading={packLoading === 'pack_25000'}
          />
        </div>
      </div>
    </div>
  )
}
